// 部門別 予算・実績対比表ページ (2026-06-12, v2: 原本PDF 1px再現 + 新規勘定トグル)
//   「PL作成ツール.xlsx」La SOFFITTA フォーマットを原本PDFと1px一致で再現(店舗別損益 予算・実績対比書)。
//   左パネル(単月 A-J)=印刷1ページ目 / 右パネル(累計 L-U)=印刷2ページ目。A4縦×2ページ(両面)、表は全高フル使用。
//   データ源は決算書作成ページ(FinancialStatementsPage)踏襲・部門別(section_id 付き trial_pl)。
//   予算は freee に無いため D1(/api/budget)。新規勘定科目は所属群へ自動挿入し赤強調(ON/OFF切替・黙殺禁止)。
//   絵文字禁止 / react-icons/md のみ / @freee_jp/vibes 準拠。

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, PageTitle, CardBase, HStack, Button, Text, Note,
  Breadcrumbs, MessageBlock, InlineSpinner,
} from '@freee_jp/vibes';
import { MdInsights, MdRefresh, MdPrint, MdWarning, MdUploadFile, MdVisibility, MdFileDownload } from 'react-icons/md';
import {
  fetchBudgetActual, BudgetActualResult, BudgetActualPanel, BudgetActualCellRow,
} from '../api/budgetActual';
import { getIncludeAdjustment, setIncludeAdjustment } from '../api/adjustmentMode';
import { VERTICAL_CATS } from '../data/budgetActualLayout';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const TERMS = [80, 79, 78];
// 仮想合算部門: 山城屋(庄藏3068549 + 分店3068550 の合算)。負の sentinel id で freee section と衝突回避。
const YAMASHIROYA_COMBINED = -549550;
const COMBINE_PARTS: Record<number, number[]> = { [YAMASHIROYA_COMBINED]: [3068549, 3068550] };
const combineFor = (id: number): number[] | undefined => COMBINE_PARTS[id];

// 原本PDF実測値をpt座標のまま使う。Chrome PDFでも CSS pt = 1/72 inch なのでA4上の位置が安定する。
const FONT = `"Noto Sans JP","Yu Gothic","Meiryo",sans-serif`;
const FONT_JP = `"Noto Sans JP","Yu Gothic","Meiryo",sans-serif`;
// 罫線太さ: 基本は細く / 太くするのは外枠・ヘッダー下・減価償却費の四辺・単独行(売上総利益/営業利益/経常利益)の上下。二重線は据置。
const LINE_THIN = 0.4;
const LINE_THICK = 1.5; // 太罫(細め=2px相当)。位置を px グリッドへスナップして AA で薄く見えないようにする
const SOLO_ROWS = new Set(['売上総利益', '営業利益', '経常利益']);                 // 上下を表幅いっぱいの太罫で独立させる
const NO_LEFT_LINE_ROWS = new Set(['純売上高', '売上原価', '売上総利益', '一般管理費', '営業利益', '経常利益', '営業外収益', '営業外費用']); // 文字左(A|B)の縦罫を消す
const BOLD_ROW = '減価償却費';                                                  // フォント太字 + 四辺太罫
const SUBTOTAL_FILL = '#BFBFBF'; // 0.75 小計行(B〜J、A列は白)
// SALES_EMPH(売上強調)は 2026-06-17 user指示(b)で廃止。
const TITLE_FILL = '#F0F0F0';    // 0.94 タイトル行 帯
const NEW_FILL = '#fee2e2';      // 新規行(画面のみ・印刷では消す)
const NEW_DOT = '#dc2626';

// 予算・実績対比の重点セル強調 (2026-07-01 user指示)。preview / 印刷 / PDF 共通で目立たせる。
//   背景色は print-color-adjust:exact により全経路(画面・印刷・PDF保存)で出力される。
//   対象行は全て小計(subtotal)=既に太字なので、ハイライト背景で更に強調する。
//   列: 4=予算 / 5=予算構成比 / 6=実績 / 7=実績構成比 / 9=対予算差
const EMPH_FILL = '#FFE066';     // 重点セル ハイライト(小計 gray #BFBFBF より優先)
const EMPH_CELLS: Record<string, ReadonlySet<number>> = {
  '売上': new Set([4, 6, 9]),            // 売上: 予算・実績・対予算差 (2026-07-15 user指示: 純売上高行→売上行へ移動)
  '売上原価': new Set([5, 7, 9]),        // 予算の構成比・実績の構成比・対予算差(構成比pt差)
  '人件費': new Set([4, 5, 6, 7, 9]),    // 予算・実績・対予算差 + 予算/実績の構成比
  '販売経費': new Set([4, 5, 6, 7, 9]),
  '営業利益': new Set([4, 5, 6, 7, 9]),
};
// 重点ハイライトの表示パターン (2026-07-15 user指示): 'gold'=黄背景+太罫囲み(従来) / 'black'=黒背景白文字・枠線なし
type EmphStyle = 'gold' | 'black';
const EMPH_STYLE_KEY = 'freeeman.budgetActual.emphStyle.v1';
const loadEmphStyle = (): EmphStyle => { try { return localStorage.getItem(EMPH_STYLE_KEY) === 'black' ? 'black' : 'gold'; } catch { return 'gold'; } };
const saveEmphStyle = (s: EmphStyle) => { try { localStorage.setItem(EMPH_STYLE_KEY, s); } catch { /* */ } };

// 新規勘定ハイライト ON/OFF + 確認済み baseline (決算書ページ ACK 機構と同形・別名前空間)
const HL_KEY = 'freeeman.budgetActual.highlightNew.v1';
const ACK_KEY = 'freeeman.budgetActual.acknowledgedLabels.v1';
const HIDDEN_KEY = 'freeeman.budgetActual.hidden.v1'; // 勘定科目 表示/非表示(section別)
const DEFAULT_HIDDEN_BY_SECTION: Record<string, string[]> = {
  // 原本PDFの「店長キュレーション版」。localStorageに同sectionの設定があればそちらを優先する。
  '3068544': [
    '本部売上', 'ネット売上', '社内転売売上',
    '煙草仕入', 'ワイン仕入諸掛', 'ワイン関連仕入', '社内仕入',
    '退職金', '寄付金',
    '市場家賃', '市場家賃振替',
    '特別利益', '特別損失', '税引前当期利益', '本部経費', '店舗経常利益',
  ],
};
const loadHl = (): boolean => { try { return localStorage.getItem(HL_KEY) !== 'off'; } catch { return true; } };
const saveHl = (on: boolean) => { try { localStorage.setItem(HL_KEY, on ? 'on' : 'off'); } catch { /* */ } };
const loadAck = (): Record<string, string[]> => { try { return JSON.parse(localStorage.getItem(ACK_KEY) || '{}'); } catch { return {}; } };
const saveAck = (a: Record<string, string[]>) => { try { localStorage.setItem(ACK_KEY, JSON.stringify(a)); } catch { /* */ } };
const loadHidden = (): Record<string, string[]> => { try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) || '{}'); } catch { return {}; } };
const saveHidden = (h: Record<string, string[]>) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(h)); } catch { /* */ } };
const hiddenLabelsFor = (hidden: Record<string, string[]>, sectionId: number): string[] => {
  const key = String(sectionId);
  return Object.prototype.hasOwnProperty.call(hidden, key) ? (hidden[key] || []) : (DEFAULT_HIDDEN_BY_SECTION[key] || []);
};

// 月度ラベル(1=10月 … 12=9月)
const FISCAL_MONTH_LABEL = ['10月', '11月', '12月', '1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月'];

interface SectionOpt { section_id: number; name: string; long_name: string; available?: number; }

// --- 数値書式 ---
const fmtCur = (v: number | null): string => {
  if (v == null) return '';
  const r = Math.round(v);
  if (r < 0) return `▲ ${Math.abs(r).toLocaleString('ja-JP')}`;
  return r.toLocaleString('ja-JP');
};
// 2026-07-01 user指示: 予算実績表で負値が「-X.XX」になるのを全て「▲ X.XX」に統一。
//   構成比(col3/5/7)・達成率(fmtDiffのachievement経路)が負になり得るため fmtRatio も▲化(fmtRatioTriangleと同一挙動)。
const fmtRatio = (v: number | null): string => {
  if (v == null) return '';
  if (v < 0) return `▲ ${Math.abs(v).toFixed(2)}`;
  return v.toFixed(2);
};
const fmtRatioTriangle = (v: number | null): string => {
  if (v == null) return '';
  if (v < 0) return `▲ ${Math.abs(v).toFixed(2)}`;
  return v.toFixed(2);
};
const fmtDiff = (v: number | null, diffType: BudgetActualCellRow['diffType']): string => {
  if (diffType === 'achievement') return fmtRatio(v);
  if (diffType === 'ratio_point') return fmtRatioTriangle(v);
  return fmtCur(v);
};

// pt座標キャンバス(595.92×842.88pt)を物理A4(210×297mm)へ正規化するスケール。
//   印刷時にChromeの用紙合わせ・@page pt解釈に依存せず、外枠=物理mm / 内側=ptキャンバス を transform で填める。
const SHEET_SCALE = 'scale(calc(210mm / 595.92pt), calc(297mm / 842.88pt))';
const S = {
  page: { background: '#f3f1ef', minHeight: '100vh', paddingBottom: '3rem' } as React.CSSProperties,
  // 外枠=物理A4(mm)。これが用紙=印刷の物理サイズを確定する。
  sheetOuter: {
    background: '#fff', margin: '0 auto 18px', width: '210mm', height: '297mm',
    position: 'relative', overflow: 'hidden', boxSizing: 'border-box', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
  } as React.CSSProperties,
  // 内側=pt座標キャンバス。外枠いっぱいに transform でスケール(=A4物理幅に一致)。
  sheet: {
    width: '595.92pt', height: '842.88pt', position: 'absolute', left: 0, top: 0,
    transformOrigin: 'top left', transform: SHEET_SCALE, fontFamily: FONT, color: '#000', background: '#fff',
  } as React.CSSProperties,
};
const SEL: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d4d2d0', borderRadius: 4 };
// 店舗選択チップ(単一=排他 / 複数=トグル 共用)
const CHIP = (on: boolean): React.CSSProperties => ({
  padding: '5px 11px', borderRadius: 16, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap',
  border: on ? '1px solid #2563eb' : '1px solid #d4d2d0',
  background: on ? '#2563eb' : '#fff', color: on ? '#fff' : '#333',
});

const COLHEADERS = ['', '勘定科目', '前年同月', '構成比(%)', '予算', '構成比(%)', '実　績', '構成比(%)', '対前年差', '対予算差'];
const COL_X = [24.0, 40.6, 108.85, 176.5, 219.8, 284.5, 327.8, 392.4, 435.7, 500.4, 571.0];
const TITLE_TOP = 20.76;
const TITLE_BOTTOM = 42.71;
const MAIN_TOP = 55.76;
const BODY_TOP = 69.41;
const BODY_BOTTOM = 819.84;
const HEADER_H = BODY_TOP - MAIN_TOP;
const pt = (n: number) => `${n.toFixed(3)}pt`;

function splitPeriod(periodLabel: string): { start: string; end: string } {
  const [startRaw, endRaw] = periodLabel.replace(/-/g, '/').split('～').map((s) => s.trim());
  return { start: startRaw || '', end: endRaw || '' };
}

function formatPeriodEnd(date: string): string {
  const m = date.match(/^(\d{4})\/0?(\d{1,2})\/0?(\d{1,2})$/);
  return m ? `${m[1]}/${Number(m[2])}/${String(Number(m[3])).padStart(2, '0')}` : date;
}

const isEmphCell = (row: BudgetActualCellRow, col: number): boolean =>
  !!(EMPH_CELLS[row.label] && EMPH_CELLS[row.label].has(col));

function fillFor(row: BudgetActualCellRow, col: number, isEffNew: boolean, emphStyle: EmphStyle): string | undefined {
  // 重点セル強調 (2026-07-01 user指示)。小計 gray より優先し、preview/印刷/PDF 共通で目立たせる。
  if (isEmphCell(row, col)) return emphStyle === 'black' ? '#000' : EMPH_FILL;
  if (isEffNew) return NEW_FILL;
  if (row.isSubtotal) return col === 0 ? undefined : SUBTOTAL_FILL;
  // 2026-06-17 user指示(b): 売上行の予算/実績/対予算差の強調(SALES_EMPH)は廃止(→重点セル強調へ再設計)。
  return undefined;
}

// 黒パターン時の重点セル文字色 (黒背景に白文字)。gold時・非対象セルは undefined (=既定の黒)。
const emphColorFor = (row: BudgetActualCellRow, col: number, emphStyle: EmphStyle): string | undefined =>
  emphStyle === 'black' && isEmphCell(row, col) ? '#fff' : undefined;

// 罫線の太さ方向の開始位置を 1px(96dpi=0.75pt) グリッドへスナップ。
//   小数pt位置だとアンチエイリアスで太罫が薄く(1px相当に)見えるため、px境界に揃えてくっきり描く。
const snapPt = (v: number): number => Math.round(v / 0.75) * 0.75;
function lineStyle(x1: number, y1: number, x2: number, y2: number, width = 0.59): React.CSSProperties {
  if (Math.abs(y1 - y2) < 0.01) {
    return {
      position: 'absolute', left: pt(Math.min(x1, x2)), top: pt(snapPt(y1 - width / 2)),
      width: pt(Math.abs(x2 - x1)), height: pt(width), background: '#000',
    };
  }
  return {
    position: 'absolute', left: pt(snapPt(x1 - width / 2)), top: pt(Math.min(y1, y2)),
    width: pt(width), height: pt(Math.abs(y2 - y1)), background: '#000',
  };
}

function cellStyle(
  left: number, top: number, width: number, height: number,
  opts: { align?: 'left' | 'center' | 'right'; bold?: boolean; fill?: string; fontSize?: string; color?: string; family?: string } = {},
): React.CSSProperties {
  return {
    position: 'absolute',
    left: pt(left),
    top: pt(top),
    width: pt(width),
    height: pt(height),
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: opts.align === 'right' ? 'flex-end' : opts.align === 'center' ? 'center' : 'flex-start',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: opts.align === 'right' ? '0 2.0pt 0 0' : opts.align === 'center' ? '0' : '0 0 0 2.1pt',
    background: opts.fill,
    color: opts.color || '#000',
    fontFamily: opts.family || FONT,
    fontSize: opts.fontSize || '8.7pt',
    fontWeight: opts.bold ? 700 : 400,
    lineHeight: 1,
  };
}

function PanelTable({ panel, store, printedAt, isEffNew, idx, hiddenSet, emphStyle }: {
  panel: BudgetActualPanel; store: string; printedAt: string; isEffNew: (r: BudgetActualCellRow) => boolean; idx: number; hiddenSet: Set<string>; emphStyle: EmphStyle;
}) {
  const rows = panel.rows.filter((r) => !hiddenSet.has(r.label)); // 表示/非表示を反映(行は動的・再配分)
  const bodyH = (BODY_BOTTOM - BODY_TOP) / Math.max(rows.length, 1);
  const isCumulative = idx === 1;
  const titleFill = isCumulative ? '#000' : TITLE_FILL;
  const titleColor = isCumulative ? '#fff' : '#000';
  const period = splitPeriod(panel.periodLabel);
  const boundaryY = (i: number) => BODY_TOP + bodyH * i;
  const colLeft = (col: number) => COL_X[col];
  const colWidth = (col: number) => COL_X[col + 1] - COL_X[col];
  const majorBoundary = (i: number): boolean => {
    if (i === 0 || i === rows.length) return true;
    const prev = rows[i - 1]?.label;
    return prev === '純売上高' || prev === '売上総利益' || prev === '営業利益' || prev === '営業外収益' || prev === '営業外費用' || prev === '経常利益';
  };
  const deprIdx = rows.findIndex((r) => r.label === BOLD_ROW); // 減価償却費の行index(boxの太罫用)
  const deprTop = deprIdx >= 0 ? boundaryY(deprIdx) : -1;
  const deprBot = deprIdx >= 0 ? boundaryY(deprIdx + 1) : -1;
  return (
    <div className="budget-sheet" style={S.sheetOuter}>
    <div className="budget-page" style={S.sheet}>
      <div style={{ position: 'absolute', left: pt(24.03), top: pt(TITLE_TOP), width: pt(546.95), height: pt(TITLE_BOTTOM - TITLE_TOP), background: titleFill }} />
      <div style={cellStyle(42.7, 24.2, 260, 15.5, { bold: true, fontSize: '12.7pt', color: titleColor, family: FONT_JP })}>{panel.title}</div>
      <div style={cellStyle(394.5, 25.7, 72, 13.5, { bold: true, fontSize: '8.7pt', color: titleColor, align: 'left' })}>{store}</div>
      <div style={cellStyle(505.5, 25.8, 50, 13.2, { fontSize: '7.9pt', color: titleColor, align: 'right' })}>{printedAt}</div>
      <div style={cellStyle(344.0, 42.6, 62.0, 13.0, { align: 'left', fontSize: '8.0pt' })}>{period.start}</div>
      <div style={cellStyle(409.7, 42.7, 9.0, 12.8, { align: 'center', family: FONT_JP, fontSize: '8.0pt' })}>～</div>
      <div style={cellStyle(455.0, 42.6, 62.0, 13.0, { align: 'left', fontSize: '8.0pt' })}>{formatPeriodEnd(period.end)}</div>

      {COLHEADERS.map((h, col) => (
        <div key={`head-${col}`} style={cellStyle(colLeft(col), MAIN_TOP, colWidth(col), HEADER_H, { align: 'center', bold: true, family: FONT_JP })}>{h}</div>
      ))}

      {rows.map((r, i) => {
        const top = boundaryY(i);
        const eff = isEffNew(r);
        // フォント: グレー行(小計)と減価償却費は太字。その他の明細は細字(通常)。タイトルは別途太字。
        const common = { bold: r.isSubtotal || r.label === BOLD_ROW, fontSize: '8.7pt' };
        return (
          <React.Fragment key={`${r.label}-${i}`}>
            <div className={eff ? 'is-new' : undefined} style={cellStyle(colLeft(0), top, colWidth(0), bodyH, { align: 'center', fill: fillFor(r, 0, eff, emphStyle), color: emphColorFor(r, 0, emphStyle), family: FONT_JP })}></div>
            <div className={eff ? 'is-new' : undefined} style={cellStyle(colLeft(1), top, colWidth(1), bodyH, { align: 'left', fill: fillFor(r, 1, eff, emphStyle), color: emphColorFor(r, 1, emphStyle), family: FONT_JP, ...common })}>
              {eff && <span className="new-dot" style={{ color: NEW_DOT, marginRight: 2, fontSize: '0.7em' }}>●</span>}
              {r.label}
            </div>
            <div style={cellStyle(colLeft(2), top, colWidth(2), bodyH, { align: 'right', fill: fillFor(r, 2, eff, emphStyle), color: emphColorFor(r, 2, emphStyle), ...common })}>{fmtCur(r.prev)}</div>
            <div style={cellStyle(colLeft(3), top, colWidth(3), bodyH, { align: 'right', fill: fillFor(r, 3, eff, emphStyle), color: emphColorFor(r, 3, emphStyle), ...common })}>{fmtRatio(r.prevRatio)}</div>
            <div style={cellStyle(colLeft(4), top, colWidth(4), bodyH, { align: 'right', fill: fillFor(r, 4, eff, emphStyle), color: emphColorFor(r, 4, emphStyle), ...common })}>{fmtCur(r.budget)}</div>
            <div style={cellStyle(colLeft(5), top, colWidth(5), bodyH, { align: 'right', fill: fillFor(r, 5, eff, emphStyle), color: emphColorFor(r, 5, emphStyle), ...common })}>{fmtRatio(r.budgetRatio)}</div>
            <div style={cellStyle(colLeft(6), top, colWidth(6), bodyH, { align: 'right', fill: fillFor(r, 6, eff, emphStyle), color: emphColorFor(r, 6, emphStyle), ...common })}>{fmtCur(r.actual)}</div>
            <div style={cellStyle(colLeft(7), top, colWidth(7), bodyH, { align: 'right', fill: fillFor(r, 7, eff, emphStyle), color: emphColorFor(r, 7, emphStyle), ...common })}>{fmtRatio(r.actualRatio)}</div>
            <div style={cellStyle(colLeft(8), top, colWidth(8), bodyH, { align: 'right', fill: fillFor(r, 8, eff, emphStyle), color: emphColorFor(r, 8, emphStyle), ...common })}>{fmtDiff(r.vsPrev, r.diffType)}</div>
            <div style={cellStyle(colLeft(9), top, colWidth(9), bodyH, { align: 'right', fill: fillFor(r, 9, eff, emphStyle), color: emphColorFor(r, 9, emphStyle), ...common })}>{fmtDiff(r.vsBudget, r.diffType)}</div>
          </React.Fragment>
        );
      })}

      {/* 縦書きカテゴリーラベル: 各カテゴリの全文字を可視行スパンに中央配置(余裕あれば自然な字間、不足時はscaleY縦圧縮)。行非表示でも文字が欠けない。 */}
      {VERTICAL_CATS.map((cat) => {
        const idxs: number[] = [];
        rows.forEach((r, i) => { if (r.excelRow >= cat.minRow && r.excelRow <= cat.maxRow) idxs.push(i); });
        if (!idxs.length) return null;
        const spanTop = boundaryY(idxs[0]);
        const spanH = boundaryY(idxs[idxs.length - 1] + 1) - spanTop;     // この縦書きラベルが占める独立セルの高さ
        const VF = 8, GAP = 3.5;                                          // pt: フォントサイズ / 程よい文字間隔
        const n = cat.label.length;
        const naturalH = n * VF + (n - 1) * GAP;                          // 自然サイズでの文字列高
        const scale = naturalH > spanH - 2 ? (spanH - 2) / naturalH : 1;  // 余裕あれば等倍(中央配置)、不足なら縦圧縮
        return (
          <div key={`vc-${cat.label}`} style={{ position: 'absolute', left: pt(colLeft(0)), top: pt(spanTop), width: pt(colWidth(0)), height: pt(spanH), display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: pt(GAP), fontFamily: FONT_JP, fontSize: `${VF}pt`, lineHeight: 1, transform: scale < 1 ? `scaleY(${scale})` : undefined, transformOrigin: 'center center' }}>
              {cat.label.split('').map((c, k) => (<span key={k} style={{ lineHeight: 1 }}>{c}</span>))}
            </div>
          </div>
        );
      })}

      {/* (1) タイトル行 独立ボックス(4辺=太) */}
      <div style={lineStyle(24, 21.06, 571, 21.06, LINE_THICK)} />
      <div style={lineStyle(24, TITLE_BOTTOM, 571, TITLE_BOTTOM, LINE_THICK)} />
      <div style={lineStyle(24, 20.76, 24, TITLE_BOTTOM, LINE_THICK)} />
      <div style={lineStyle(571, 20.76, 571, TITLE_BOTTOM, LINE_THICK)} />
      {/* (2) 期間(日付)行: 左右の罫線なし。上=タイトル箱下 / 下=表フレーム上(下記) */}

      {/* (3) 表フレーム(列見出し含む 4辺=太) + (4) ヘッダー下=太 */}
      <div style={lineStyle(24, MAIN_TOP, 571, MAIN_TOP, LINE_THICK)} />
      <div style={lineStyle(24, BODY_BOTTOM, 571, BODY_BOTTOM, LINE_THICK)} />
      <div style={lineStyle(24, MAIN_TOP, 24, BODY_BOTTOM, LINE_THICK)} />
      <div style={lineStyle(571, MAIN_TOP, 571, BODY_BOTTOM, LINE_THICK)} />
      <div style={lineStyle(24.32, BODY_TOP, 107.97, BODY_TOP, LINE_THICK)} />
      <div style={lineStyle(109.75, BODY_TOP, 570, BODY_TOP, LINE_THICK)} />

      {/* 縦罫(細): C|D, D|E..I|J / B|C=二重(据置) */}
      <div style={lineStyle(176.48, MAIN_TOP, 176.48, BODY_BOTTOM, LINE_THIN)} />
      {[219.79, 284.45, 327.76, 392.42, 435.72, 500.38].map((x) => (
        <div key={`vl-${x}`} style={lineStyle(x, MAIN_TOP, x, BODY_BOTTOM, LINE_THIN)} />
      ))}
      <div style={lineStyle(108.26, MAIN_TOP, 108.26, BODY_BOTTOM, 0.59)} />
      <div style={lineStyle(109.45, MAIN_TOP, 109.45, BODY_BOTTOM, 0.59)} />
      {/* A|B 縦罫(細): ヘッダー部 + 各データ行(NO_LEFT行・減価償却費は除く) */}
      <div style={lineStyle(40.64, MAIN_TOP, 40.64, BODY_TOP, LINE_THIN)} />
      {rows.map((r, i) => (
        (NO_LEFT_LINE_ROWS.has(r.label) || r.label === BOLD_ROW) ? null
          : <div key={`ab-${i}`} style={lineStyle(40.64, boundaryY(i), 40.64, boundaryY(i + 1), LINE_THIN)} />
      ))}

      {/* 横罫(細): データ行境界。SOLO行(売上総利益/営業利益/経常利益)は全幅(細)で独立。減価償却費の上下/枠下は別途 */}
      {Array.from({ length: rows.length + 1 }, (_, i) => {
        if (i === 0 || i === rows.length) return null;        // 上=ヘッダー下(済) / 下=表下枠(済)
        if (i === deprIdx || i === deprIdx + 1) return null;  // 減価償却費の上下は box で描画
        const a = rows[i - 1]; const b = rows[i];
        const adjSolo = !!(a && SOLO_ROWS.has(a.label)) || !!(b && SOLO_ROWS.has(b.label));
        const full = adjSolo || majorBoundary(i);
        const y = boundaryY(i);
        return (
          <React.Fragment key={`hl-${i}`}>
            {full && <div style={lineStyle(24.32, y, 40.34, y, LINE_THIN)} />}
            <div style={lineStyle(40.34, y, 107.97, y, LINE_THIN)} />
            <div style={lineStyle(109.75, y, 570, y, LINE_THIN)} />
          </React.Fragment>
        );
      })}

      {/* (5) 減価償却費 box(太): B列左端(40.64)〜右端の上下左の3辺。A列の縦書き(一般管理費)には侵入しない。右は表右枠が担う */}
      {deprIdx >= 0 && (
        <>
          <div style={lineStyle(40.64, deprTop, 571, deprTop, LINE_THICK)} />
          <div style={lineStyle(40.64, deprBot, 571, deprBot, LINE_THICK)} />
          <div style={lineStyle(40.64, deprTop, 40.64, deprBot, LINE_THICK)} />
        </>
      )}

      {/* 重点セル強調(2026-07-01 user指示): 各対象セルを個別に太罫(LINE_THICK)で全周(上下左右)囲む。
          lineStyle は座標中心に線を描くので、隣接する強調セルの共有辺も単一の 1.5pt 線になり、
          全セル・全辺が均一の太さになる(結合せず1セル=1枠。隣接部の内側罫も強調される)。
          gridline の後に描画=上。白黒印刷でも明確(print-color-adjust:exact)。背景ゴールドはカラー時の追加強調。
          2026-07-15 user指示: 'black'パターン(黒背景白文字)では枠線を描かない。 */}
      {emphStyle === 'gold' && rows.flatMap((r, i) => {
        const cols = EMPH_CELLS[r.label];
        if (!cols) return [];
        const y1 = boundaryY(i);
        const y2 = boundaryY(i + 1);
        return Array.from(cols).flatMap((c) => {
          const x1 = colLeft(c);
          const x2 = colLeft(c + 1);
          return [
            <div key={`emph-${r.label}-${i}-${c}-t`} style={lineStyle(x1, y1, x2, y1, LINE_THICK)} />,
            <div key={`emph-${r.label}-${i}-${c}-b`} style={lineStyle(x1, y2, x2, y2, LINE_THICK)} />,
            <div key={`emph-${r.label}-${i}-${c}-l`} style={lineStyle(x1, y1, x1, y2, LINE_THICK)} />,
            <div key={`emph-${r.label}-${i}-${c}-r`} style={lineStyle(x2, y1, x2, y2, LINE_THICK)} />,
          ];
        });
      })}
    </div>
    </div>
  );
}

const PRINT_CSS = `
.budget-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
@media print {
  html { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  /* 印刷レイアウト幅を物理A4(210mm)に固定。visibility:hidden の要素も幅を占有するため、
     これを入れないとページが横に広がり sheet が左上で縮小される(右下に余白) */
  html, body { width: 210mm !important; min-width: 0 !important; max-width: 210mm !important;
    margin: 0 !important; padding: 0 !important; background: #fff !important; overflow: visible !important; }
  #root, #root > div, .vb-container, .vb-container--responsive {
    width: 210mm !important; min-width: 0 !important; max-width: 210mm !important; margin: 0 !important; padding: 0 !important; }
  body * { visibility: hidden; }
  #budget-print, #budget-print * { visibility: visible; }
  .vb-container { position: static !important; }
  .vb-container > :not(#budget-print) { display: none !important; }
  #budget-print { position: absolute; left: 0; top: 0; width: 210mm !important; margin: 0 !important; padding: 0 !important; }
  .no-print { display: none !important; }
  /* vibes の上部グローバルナビは visibility:hidden でも幅(約1168px)を占有し、印刷ページが横に広がって
     用紙合わせで内容が約0.68倍に縮小される主因。印刷では layout から完全除去する。 */
  .vb-globalNavi, nav.vb-globalNavi, [class*="globalNavi"], [class*="GlobalNavi"] { display: none !important; }
  /* 用紙=物理A4。外枠(.budget-sheet)を 210×297mm で確定し、内側ptキャンバスは transform で填める */
  @page { size: A4 portrait; margin: 0; }
  .budget-sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important;
    width: 210mm !important; height: 297mm !important; overflow: hidden !important; box-sizing: border-box;
    break-after: page; page-break-after: always; }
  /* 全シートの後でページ送り、最後の1枚だけ送らない → 1店舗(2枚)でも全店舗一括(2N枚)でも正しく改ページ */
  .budget-sheet:last-child  { break-after: auto; page-break-after: auto; }
  .budget-page .is-new { background: #fff !important; }
  .budget-page .new-dot { display: none !important; }
}
`;

export const BudgetActualPage: React.FC = () => {
  const [sections, setSections] = useState<SectionOpt[]>([]);
  const [sectionId, setSectionId] = useState<number>(3068544); // 既定 La SOFFITTA
  const [term, setTerm] = useState<number>(80);
  const [month, setMonth] = useState<number>(6); // 既定 6=3月度
  const [result, setResult] = useState<BudgetActualResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [highlightNew, setHighlightNew] = useState<boolean>(() => loadHl());
  const [acknowledged, setAcknowledged] = useState<Record<string, string[]>>(() => loadAck());
  const [hidden, setHidden] = useState<Record<string, string[]>>(() => loadHidden());
  const [showSettings, setShowSettings] = useState(false);
  const [hiddenDirty, setHiddenDirty] = useState(false); // 表示項目の未保存変更フラグ
  // 店舗選択モード(単一=排他切替 / 複数=ON/OFFトグルで一括印刷)
  const [selMode, setSelMode] = useState<'single' | 'multi'>('single');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkResults, setBulkResults] = useState<BudgetActualResult[] | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingPrint, setPendingPrint] = useState(false);
  const [includeAdj, setIncludeAdj] = useState<boolean>(() => getIncludeAdjustment()); // 整理仕訳 含む/除く

  // 勘定科目 表示/非表示(section別・localStorage 永続。決算書ページと同形)
  const hiddenSet = useMemo(() => new Set(hiddenLabelsFor(hidden, sectionId)), [hidden, sectionId]);
  // 表示/非表示は localStorage キャッシュ + サーバー(全デバイス共通)に保存。
  const persistHidden = useCallback((next: Record<string, string[]>) => {
    saveHidden(next);
    fetch(`${API_BASE}/api/budget-actual/prefs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ hidden: next }) }).catch(() => { /* オフライン時はキャッシュのみ */ });
  }, []);
  // トグル/全表示は in-memory 変更のみ(即保存しない)。永続化は「保存」ボタンから。
  const toggleHidden = useCallback((label: string) => {
    setHidden((h) => {
      const key = String(sectionId);
      const cur = new Set(hiddenLabelsFor(h, sectionId));
      if (cur.has(label)) cur.delete(label); else cur.add(label);
      return { ...h, [key]: Array.from(cur) };
    });
    setHiddenDirty(true);
  }, [sectionId]);
  const showAllRows = useCallback(() => {
    setHidden((h) => ({ ...h, [String(sectionId)]: [] }));
    setHiddenDirty(true);
  }, [sectionId]);
  // 「保存」= localStorage + サーバー(全デバイス共通)へ永続化。
  const saveHiddenPrefs = useCallback(() => {
    persistHidden(hidden);
    setHiddenDirty(false);
  }, [hidden, persistHidden]);

  useEffect(() => {
    fetch(`${API_BASE}/api/sections`).then((r) => r.json()).then((d) => {
      const arr: SectionOpt[] = Array.isArray(d) ? d : (d.sections || d.rows || []);
      setSections(arr.filter((s) => s.available !== 0 && s.section_id));
    }).catch(() => { /* ignore */ });
  }, []);

  // 表示/非表示設定をサーバー(全デバイス共通)から読み込み(localStorage 初期値を上書き)。
  useEffect(() => {
    fetch(`${API_BASE}/api/budget-actual/prefs`).then((r) => r.ok ? r.json() : null).then((d) => {
      if (d && d.hidden && typeof d.hidden === 'object') { setHidden(d.hidden); saveHidden(d.hidden); setHiddenDirty(false); }
    }).catch(() => { /* サーバー不通時は localStorage 値を継続使用 */ });
  }, []);

  // 表示用 店舗リスト: 山城屋分店(3068550)の直後に「山城屋(合算)」仮想部門を挿入 → 庄藏/分店/山城屋 の順。
  const displaySections = useMemo(() => {
    const out: SectionOpt[] = [];
    for (const s of sections) {
      out.push(s);
      if (s.section_id === 3068550) out.push({ section_id: YAMASHIROYA_COMBINED, name: '山城屋', long_name: '山城屋(合算)', available: 1 });
    }
    return out;
  }, [sections]);

  const storeName = useCallback((): string => {
    if (sectionId === 0) return '全店舗';
    if (sectionId === YAMASHIROYA_COMBINED) return '山城屋';
    const s = sections.find((x) => x.section_id === sectionId);
    return s ? s.long_name : `部門${sectionId}`;
  }, [sectionId, sections]);

  // PDF保存: 保存ファイル名を「○期○月 店舗名」にして印刷ダイアログ(PDFに保存)を開く。
  //   Chrome は「PDFに保存」既定ファイル名に document.title を使うため、印刷前に一時的に差し替える。
  const savePdf = useCallback(() => {
    if (!result) return;
    const monthLabel = FISCAL_MONTH_LABEL[month - 1] || `${month}月`;
    const fname = `${term}期${monthLabel} ${result.storeName}`;
    const prevTitle = document.title;
    document.title = fname;
    const restore = () => { document.title = prevTitle; window.removeEventListener('afterprint', restore); };
    window.addEventListener('afterprint', restore);
    window.print();
  }, [result, term, month]);

  const nameOf = useCallback((id: number): string => {
    if (id === 0) return '全店舗';
    if (id === YAMASHIROYA_COMBINED) return '山城屋';
    const s = sections.find((x) => x.section_id === id);
    return s ? s.long_name : `部門${id}`;
  }, [sections]);

  const toggleSelected = useCallback((id: number) => {
    setSelectedIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }, []);

  // 選択した全店舗を sections の並び順で取得 → bulkResults に積む(複数選択モードの「生成」「一括印刷」共通)。
  //   各店=2枚(単月/累計)、改ページCSS(.budget-sheet)で 2N ページの両面PDFになる。
  const loadSelected = useCallback(async (doPrint: boolean) => {
    if (!selectedIds.length) { setError('店舗が選択されていません'); return; }
    setBulkBusy(true); setLoading(true); setError(null); setResult(null);
    try {
      const ordered = displaySections.map((s) => s.section_id).filter((id) => selectedIds.includes(id));
      const out: BudgetActualResult[] = [];
      for (const id of ordered) {
        try {
          const r = await fetchBudgetActual(term, month, id, nameOf(id), combineFor(id));
          if (!r.meta.empty) out.push(r);
        } catch { /* 取得失敗店はスキップ(他店は止めない) */ }
      }
      if (!out.length) { setError('選択店舗の実績が freee から取得できませんでした'); setBulkResults(null); return; }
      setBulkResults(out);
      if (doPrint) setPendingPrint(true);
    } finally {
      setBulkBusy(false); setLoading(false);
    }
  }, [selectedIds, displaySections, term, month, nameOf]);

  const generate = useCallback(async () => {
    if (selMode === 'multi') { await loadSelected(false); return; } // 複数選択: 選択全店を画面に生成(印刷なし)
    setLoading(true); setError(null); setBulkResults(null);
    try {
      const r = await fetchBudgetActual(term, month, sectionId, storeName(), combineFor(sectionId));
      setResult(r);
    } catch (e: any) {
      setError(e.message || String(e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [selMode, loadSelected, term, month, sectionId, storeName]);

  const bulkPrint = useCallback(() => loadSelected(true), [loadSelected]);

  // bulkResults 描画完了後に印刷(setTimeout で1フレーム待つ)
  useEffect(() => {
    if (!pendingPrint || !bulkResults) return;
    const monthLabel = FISCAL_MONTH_LABEL[month - 1] || `${month}月`;
    const prevTitle = document.title;
    document.title = `${term}期${monthLabel} 全${bulkResults.length}店舗`;
    const restore = () => { document.title = prevTitle; window.removeEventListener('afterprint', restore); };
    window.addEventListener('afterprint', restore);
    const t = setTimeout(() => { window.print(); setPendingPrint(false); }, 120);
    return () => clearTimeout(t);
  }, [pendingPrint, bulkResults, term, month]);

  // 予算取込: 1行=「表示項目<tab/カンマ>10月..9月(12数値)」。La SOFFITTA 出力形式準拠。
  const doImport = useCallback(async () => {
    setImportBusy(true); setImportMsg(null);
    try {
      const rows: { display_item: string; fiscal_month: number; amount: number }[] = [];
      for (const line of importText.split('\n')) {
        const cells = line.split(/[\t,]/).map((c) => c.trim());
        if (cells.length < 2 || !cells[0]) continue;
        const item = cells[0];
        for (let fm = 1; fm <= 12 && fm < cells.length; fm++) {
          const raw = cells[fm].replace(/[, ¥円]/g, '');
          if (raw === '') continue;
          const amt = Number(raw);
          if (!Number.isFinite(amt)) continue;
          rows.push({ display_item: item, fiscal_month: fm, amount: amt });
        }
      }
      if (!rows.length) { setImportMsg('取込対象の行がありません'); setImportBusy(false); return; }
      const res = await fetch(`${API_BASE}/api/budget/bulk-upsert`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fiscal_term: term, section_id: sectionId, rows, source: 'excel_import', updated_by: 'web_app_user' }),
      });
      const j = await res.json();
      setImportMsg(`予算 ${j.upserted ?? 0} 件を取込みました(${storeName()} / ${term}期)。`);
      await generate();
    } catch (e: any) {
      setImportMsg(`取込失敗: ${e.message || e}`);
    } finally {
      setImportBusy(false);
    }
  }, [importText, term, sectionId, storeName, generate]);

  // 新規(テンプレ外)勘定科目で baseline 未確認のラベル集合(両パネル横断)
  const newLabelsFor = useCallback((res: BudgetActualResult | null): Set<string> => {
    const s = new Set<string>(); if (!res) return s;
    const ack = new Set(acknowledged[String(res.sectionId)] || []);
    for (const p of [res.single, res.cumulative])
      for (const r of p.rows) if (r.isNew && r.label && !ack.has(r.label)) s.add(r.label);
    return s;
  }, [acknowledged]);

  const acknowledgeNew = useCallback((res: BudgetActualResult | null) => {
    if (!res) return;
    setAcknowledged((a) => {
      const key = String(res.sectionId); const cur = new Set(a[key] || []);
      for (const p of [res.single, res.cumulative])
        for (const r of p.rows) if (r.isNew && r.label) cur.add(r.label);
      const next = { ...a, [key]: Array.from(cur) }; saveAck(next); return next;
    });
  }, []);

  const toggleHighlight = useCallback(() => {
    setHighlightNew((on) => { const next = !on; saveHl(next); return next; });
  }, []);

  // 重点ハイライトの表示パターン切替 (2026-07-15 user指示): 黄色(枠線あり) / 黒背景白文字(枠線なし)
  const [emphStyle, setEmphStyle] = useState<EmphStyle>(() => loadEmphStyle());
  const toggleEmphStyle = useCallback(() => {
    setEmphStyle((cur) => { const next: EmphStyle = cur === 'gold' ? 'black' : 'gold'; saveEmphStyle(next); return next; });
  }, []);

  const unmapped = result?.meta.unmapped || [];
  const newLabelsActive = newLabelsFor(result);
  const isEffNew = useCallback(
    (r: BudgetActualCellRow) => highlightNew && !!r.isNew && newLabelsActive.has(r.label),
    [highlightNew, newLabelsActive]);

  return (
    <div style={S.page}>
      <Container>
        <Breadcrumbs links={[{ url: '#', title: 'FREEEMAN' }, { url: '#budget_actual', title: '部門別予算実績' }]} />
        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <HStack gap={2} alignItems="center">
            <MdInsights size={28} color="#2563eb" />
            <PageTitle>部門別 予算・実績対比表</PageTitle>
          </HStack>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Note>
            「PL作成ツール」La SOFFITTA フォーマットの店舗別 予算・実績対比書を、freee から部門別に自動取得して生成します（実績・前年=freee／予算=登録値）。
            左=単月／右=累計の2パネルで、印刷ボタンで A4縦×2ページ（両面・表は下端までフル）に出力できます。テンプレ外の新規勘定科目は所属群へ自動挿入し、ハイライトのON/OFFで強調表示を切替できます。
          </Note>
        </div>

        <CardBase>
          <div style={{ padding: 16 }} className="no-print">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'inline-flex', border: '1px solid #d4d2d0', borderRadius: 4, overflow: 'hidden' }} title="店舗選択モード">
                <button onClick={() => { setSelMode('single'); setBulkResults(null); }} style={{ padding: '6px 12px', border: 'none', cursor: 'pointer', background: selMode === 'single' ? '#2563eb' : '#fff', color: selMode === 'single' ? '#fff' : '#333', fontSize: '0.85rem' }}>単一選択</button>
                <button onClick={() => setSelMode('multi')} style={{ padding: '6px 12px', border: 'none', borderLeft: '1px solid #d4d2d0', cursor: 'pointer', background: selMode === 'multi' ? '#2563eb' : '#fff', color: selMode === 'multi' ? '#fff' : '#333', fontSize: '0.85rem' }}>複数選択</button>
              </div>
              <select value={term} onChange={(e) => setTerm(Number(e.target.value))} style={SEL} title="会計期">
                {TERMS.map((t) => (<option key={t} value={t}>{t}期</option>))}
              </select>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={SEL} title="対象月度">
                {FISCAL_MONTH_LABEL.map((lbl, i) => (<option key={i} value={i + 1}>{i + 1}月度（{lbl}）</option>))}
              </select>
              <select value={includeAdj ? '1' : '0'} onChange={(e) => { const v = e.target.value === '1'; setIncludeAdjustment(v); setIncludeAdj(v); }} style={SEL}
                title="決算整理仕訳の扱い。決算月(9月)は減価償却費の年間精算が整理仕訳で入るため、月次比較は「除く」、決算書・年間合計は「含む」">
                <option value="1">整理仕訳 含む</option>
                <option value="0">整理仕訳 除く</option>
              </select>
              <Button appearance="primary" onClick={generate} disabled={loading || (selMode === 'multi' && !selectedIds.length)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 56, justifyContent: 'center' }}>
                  <span style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {loading ? <InlineSpinner isLoading={true} /> : <MdRefresh size={16} />}
                  </span>
                  {selMode === 'multi' ? `生成（${selectedIds.length}店）` : '生成'}
                </span>
              </Button>
              <Button onClick={() => window.print()} disabled={!result}>
                <MdPrint /> 印刷
              </Button>
              <Button appearance="primary" onClick={savePdf} disabled={!result}>
                <MdFileDownload /> PDFダウンロード
              </Button>
              {selMode === 'multi' && (
                <Button appearance="primary" onClick={bulkPrint} disabled={bulkBusy || !selectedIds.length}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {bulkBusy ? <InlineSpinner isLoading={true} /> : <MdPrint size={16} />}
                    一括印刷（{selectedIds.length}店舗）
                  </span>
                </Button>
              )}
              <Button onClick={toggleEmphStyle} disabled={!result && !bulkResults}>
                重点ハイライト：{emphStyle === 'gold' ? '黄色+枠線' : '黒背景白文字'}
              </Button>
              <Button onClick={toggleHighlight} appearance={highlightNew ? 'primary' : undefined} disabled={!result}>
                <MdVisibility /> 新規勘定ハイライト：{highlightNew ? 'ON' : 'OFF'}
              </Button>
              {highlightNew && newLabelsActive.size > 0 && (
                <button onClick={() => acknowledgeNew(result)}
                  style={{ padding: '6px 12px', borderRadius: 4, border: `1px solid ${NEW_DOT}`, background: '#fff', color: NEW_DOT, cursor: 'pointer', fontSize: '0.85rem' }}>
                  新規 {newLabelsActive.size} 件を確認済みにする
                </button>
              )}
              <Button onClick={() => setShowImport((s) => !s)}>
                <MdUploadFile /> 予算取込
              </Button>
              <Button onClick={() => setShowSettings((s) => !s)} disabled={!result}>
                <MdVisibility /> 表示する勘定科目
              </Button>
              {hiddenSet.size > 0 && (
                <button onClick={showAllRows}
                  style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #d4d2d0', background: '#fff', cursor: 'pointer', fontSize: '0.85rem' }}>
                  すべて表示（{hiddenSet.size}件 非表示中）
                </button>
              )}
            </div>

            {/* 店舗選択（単一=クリックで排他切替 / 複数=クリックでON/OFFトグル → 一括印刷） */}
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} className="no-print">
              <span style={{ fontSize: '0.82rem', color: '#6b7280', marginRight: 2 }}>店舗:</span>
              {selMode === 'multi' && (<>
                <button onClick={() => setSelectedIds(sections.map((s) => s.section_id))} style={CHIP(false)}>全選択</button>
                <button onClick={() => setSelectedIds([])} style={CHIP(false)}>全解除</button>
                <span style={{ width: 1, height: 20, background: '#d4d2d0', margin: '0 4px' }} />
              </>)}
              {selMode === 'single' && (
                <button onClick={() => setSectionId(0)} style={CHIP(sectionId === 0)}>全店舗(合算)</button>
              )}
              {displaySections.map((s) => {
                const on = selMode === 'single' ? sectionId === s.section_id : selectedIds.includes(s.section_id);
                return (
                  <button key={s.section_id}
                    onClick={() => (selMode === 'single' ? setSectionId(s.section_id) : toggleSelected(s.section_id))}
                    style={CHIP(on)}>{s.long_name}</button>
                );
              })}
              {selMode === 'multi'
                ? <span style={{ fontSize: '0.82rem', color: '#2563eb', fontWeight: 700, marginLeft: 4 }}>選択 {selectedIds.length} 店舗</span>
                : <span style={{ fontSize: '0.78rem', color: '#9ca3af', marginLeft: 4 }}>クリックで切替。複数店を一括印刷するには「複数選択」モードへ</span>}
            </div>

            {showSettings && result && (
              <div style={{ marginTop: 12, padding: 12, border: '1px solid #d4d2d0', borderRadius: 6, background: '#fafafa' }}>
                <Text weight="bold">表示する勘定科目（チェックを外すと非表示・印刷にも反映。原本のように不要な行を隠せます。変更後は下の「保存」で確定＝全デバイスに反映）</Text>
                <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2px 16px', maxHeight: 360, overflowY: 'auto' }}>
                  {Array.from(new Map(result.single.rows.filter((r) => r.label).map((r) => [r.label, r])).values()).map((r) => {
                    const off = hiddenSet.has(r.label);
                    return (
                      <label key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer', padding: '2px 4px', paddingLeft: `${4 + r.indent * 12}px` }}>
                        <input type="checkbox" checked={!off} onChange={() => toggleHidden(r.label)} />
                        <span style={{ fontWeight: r.isSubtotal ? 700 : 400, color: off ? '#aaa' : '#222' }}>{r.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Button appearance="primary" onClick={saveHiddenPrefs} disabled={!hiddenDirty}>表示項目を保存</Button>
                  {hiddenDirty
                    ? <span style={{ fontSize: '0.8rem', color: '#c67320', fontWeight: 700 }}>未保存の変更があります</span>
                    : <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>保存済（全デバイス共通）</span>}
                </div>
              </div>
            )}

            {error && <div style={{ marginTop: 12 }}><MessageBlock success={false}>{error}</MessageBlock></div>}

            {showImport && (
              <div style={{ marginTop: 12, padding: 12, border: '1px solid #d4d2d0', borderRadius: 6, background: '#fafafa' }}>
                <Text weight="bold">予算取込（{storeName()} / {term}期）</Text>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', margin: '6px 0' }}>
                  1行 = 「表示項目」+ 10月〜9月の12数値（タブ/カンマ区切り）。例: <code>給与手当,1939000,1939000,…,2009000</code>。
                  表示項目名は予実表の科目名（売上／材料仕入／給与手当／家賃／運賃 等）。
                </div>
                <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={6}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem', padding: 8, border: '1px solid #d4d2d0', borderRadius: 4 }}
                  placeholder={'売上,9009572,9486281,12486232,8055915,7444779,7745249,8881118,9336582,9053358,9381581,9416496,8703270'} />
                <div style={{ marginTop: 8 }}>
                  <Button appearance="primary" onClick={doImport} disabled={importBusy}>
                    {importBusy ? <InlineSpinner isLoading={true} /> : <MdUploadFile />} 取込実行
                  </Button>
                  {importMsg && <span style={{ marginLeft: 12, fontSize: '0.82rem' }}>{importMsg}</span>}
                </div>
              </div>
            )}

            {result?.meta.budgetMissing && (
              <div style={{ marginTop: 12 }}>
                <MessageBlock success={false}>この部門の予算データが未登録です（予算列=0）。「予算取込」または「店舗予算作成」から登録してください。</MessageBlock>
              </div>
            )}
            {highlightNew && unmapped.length > 0 && (
              <div style={{ marginTop: 12, fontSize: '0.8rem' }}>
                <MessageBlock success={false}>
                  <HStack gap={1} alignItems="center"><MdWarning /> <Text weight="bold">テンプレ外の新規勘定科目（所属群へ自動挿入・赤強調）</Text></HStack>
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {unmapped.map((u, i) => (<li key={i}>{u.accountName}（freee表示名: {u.categoryName} / 当月 {fmtCur(u.amount)}）</li>))}
                  </ul>
                </MessageBlock>
              </div>
            )}
          </div>
        </CardBase>

        <div id="budget-print" style={{ marginTop: 16 }}>
          {loading && (
            <div style={{ padding: 32, textAlign: 'center' }} className="no-print">
              <HStack gap={1} alignItems="center" justifyContent="center">
                <InlineSpinner isLoading={true} /> <Text>freee から取得・整形中...</Text>
              </HStack>
            </div>
          )}
          {!loading && bulkResults && bulkResults.length > 0 && bulkResults.map((br) => (
            <React.Fragment key={br.sectionId}>
              <PanelTable panel={br.single} store={br.storeName} printedAt={br.printedAt} isEffNew={() => false} idx={0} hiddenSet={new Set(hiddenLabelsFor(hidden, br.sectionId))} emphStyle={emphStyle} />
              <PanelTable panel={br.cumulative} store={br.storeName} printedAt={br.printedAt} isEffNew={() => false} idx={1} hiddenSet={new Set(hiddenLabelsFor(hidden, br.sectionId))} emphStyle={emphStyle} />
            </React.Fragment>
          ))}
          {!loading && !bulkResults && result && !result.meta.empty && (
            <>
              <PanelTable panel={result.single} store={result.storeName} printedAt={result.printedAt} isEffNew={isEffNew} idx={0} hiddenSet={hiddenSet} emphStyle={emphStyle} />
              <PanelTable panel={result.cumulative} store={result.storeName} printedAt={result.printedAt} isEffNew={isEffNew} idx={1} hiddenSet={hiddenSet} emphStyle={emphStyle} />
            </>
          )}
          {!loading && !bulkResults && result && result.meta.empty && (
            <div style={{ padding: 24 }} className="no-print">
              <MessageBlock success={false}>この部門・期間の実績データが freee から取得できませんでした（空応答）。</MessageBlock>
            </div>
          )}
          {!loading && !bulkResults && !result && (
            <div style={{ padding: 32, textAlign: 'center', color: '#888' }} className="no-print">
              <Text>部門・会計期・月度を選んで「生成」を押してください。</Text>
            </div>
          )}
        </div>
      </Container>
      <style>{PRINT_CSS}</style>
    </div>
  );
};

export default BudgetActualPage;
